/**
 * TitanSkillEffect — Grit ironbody-path fork: full crystallization stance.
 *
 * Plant your feet for 4 seconds — completely immobile. Incoming damage
 * halved (50% DR), melee hits deal ×2.0 damage with massive knockback.
 * Cannot be cancelled during the active phase.
 *
 * Phases: windup → active(4000ms) → recovery → cooldown.
 * Non-interruptible during active (PhasedSkillEffect default).
 *
 * Supports upgrade keys:
 * - Inexorable: `durationMs` (additive, extends active phase)
 * - Shockwave: `activationShockwaveRange` (max) + `activationShockwaveDamage` (additive)
 *
 * Extends PhasedSkillEffect for phase lifecycle.
 * Pure logic — no Phaser dependencies.
 *
 * @see ADR-064 (attunement shared systems architecture)
 * @see grit-spec.md § Titan
 */

import { PhasedSkillEffect, type SkillPhase } from '../../PhasedSkillEffect.js';

// ---------------------------------------------------------------------------
// Dependency interfaces
// ---------------------------------------------------------------------------

export interface TitanDeps {
  readonly resolver: {
    getAdditiveEffect(skillId: string, key: string): number;
    getMaxEffect(skillId: string, key: string): number | undefined;
    hasEffect(skillId: string, key: string): boolean;
  };
  readonly statusEffects: {
    apply(statusId: string, ...args: unknown[]): void;
    remove(statusId: string, ...args: unknown[]): void;
  };
}

// ---------------------------------------------------------------------------
// Config
// ---------------------------------------------------------------------------

export interface TitanConfig {
  readonly windupMs: number;
  readonly activeMs: number;
  readonly recoveryMs: number;
  readonly cooldownMs: number;
  readonly baseDamageReduction: number;
  readonly meleeDamageMultiplier: number;
}

const DEFAULT_CONFIG: TitanConfig = {
  windupMs: 200,
  activeMs: 4000,            // 4-second crystallization
  recoveryMs: 300,
  cooldownMs: 1600,          // long cooldown tier
  baseDamageReduction: 0.5,  // 50% incoming DR
  meleeDamageMultiplier: 2.0,
};

// ---------------------------------------------------------------------------
// Skill effect
// ---------------------------------------------------------------------------

export class TitanSkillEffect extends PhasedSkillEffect {
  readonly skillId = 'titan';

  protected readonly phaseSequence: readonly SkillPhase[] =
    ['windup', 'active', 'recovery', 'cooldown'];

  private readonly deps: TitanDeps;
  private readonly config: TitanConfig;

  // Resolved values
  private _resolvedActiveMs = 0;
  private _buffApplied = false;
  private _meleeHitsLanded = 0;
  private _shockwaveFired = false;
  private _shockwaveRange = 0;
  private _shockwaveDamage = 0;

  constructor(deps: TitanDeps, config: Partial<TitanConfig> = {}) {
    super();
    this.deps = deps;
    this.config = { ...DEFAULT_CONFIG, ...config };
  }

  // --- State getters ---

  get buffApplied(): boolean { return this._buffApplied; }
  get meleeHitsLanded(): number { return this._meleeHitsLanded; }
  get resolvedActiveMs(): number { return this._resolvedActiveMs; }
  get meleeDamageMultiplier(): number { return this.config.meleeDamageMultiplier; }
  get shockwaveFired(): boolean { return this._shockwaveFired; }
  get shockwaveRange(): number { return this._shockwaveRange; }
  get shockwaveDamage(): number { return this._shockwaveDamage; }

  // --- PhasedSkillEffect interface ---

  protected getPhaseDuration(phase: SkillPhase): number {
    switch (phase) {
      case 'windup': return this.config.windupMs;
      case 'active': return this._resolvedActiveMs;
      case 'recovery': return this.config.recoveryMs;
      case 'cooldown': return this.config.cooldownMs;
      default: return 0;
    }
  }

  protected onActivate(): void {
    this._buffApplied = false;
    this._meleeHitsLanded = 0;
    this._shockwaveFired = false;
    this._shockwaveRange = 0;
    this._shockwaveDamage = 0;

    // Resolve active duration (base + Inexorable bonus)
    const bonusDurationMs = this.deps.resolver.getAdditiveEffect('titan', 'durationMs');
    this._resolvedActiveMs = this.config.activeMs + bonusDurationMs;
  }

  protected onPhaseEnter(phase: SkillPhase): void {
    if (phase === 'active') {
      this.applyBuff();
      this.resolveShockwave();
    } else if (phase === 'recovery') {
      this.removeBuff();
    }
  }

  destroy(): void {
    this.removeBuff();
    super.destroy();
  }

  // --- External event handlers ---

  /**
   * Called by the game layer when the player lands a melee hit while
   * Titan is active. Tracks the hit for shrapnel emission and multiplier
   * application at the game layer.
   */
  handlePlayerMeleeHit(): void {
    if (this.phase !== 'active') return;
    this._meleeHitsLanded++;
  }

  // --- Internal ---

  private applyBuff(): void {
    this.deps.statusEffects.apply(
      'titan_active',
      -1, // permanent until phase ends
      {
        damageReduction: this.config.baseDamageReduction,
        meleeDamageMultiplier: this.config.meleeDamageMultiplier,
        movementSpeedMultiplier: 0, // fully immobilized
      },
      'titan',
    );
    this._buffApplied = true;
  }

  private resolveShockwave(): void {
    if (!this.deps.resolver.hasEffect('titan', 'activationShockwaveRange')) return;

    this._shockwaveRange = this.deps.resolver.getMaxEffect('titan', 'activationShockwaveRange') ?? 0;
    this._shockwaveDamage = this.deps.resolver.getAdditiveEffect('titan', 'activationShockwaveDamage');
    this._shockwaveFired = true;

    // Emit shockwave via status effects — game layer applies AoE
    this.deps.statusEffects.apply(
      'titan_shockwave',
      0,
      { range: this._shockwaveRange, damage: this._shockwaveDamage },
      'titan',
    );
  }

  private removeBuff(): void {
    if (this._buffApplied) {
      this.deps.statusEffects.remove('titan_active', 'titan');
      this._buffApplied = false;
    }
  }
}
